import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, errorMessage } from '@/lib/api';
import { findAccount, useMe, useWorkspace } from '@/lib/hooks';
import { intlLocale, locale, t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

// Web Speech API: Chrome/Edge/Safari expose it (Chrome with the webkit prefix),
// Firefox doesn't. TypeScript's DOM lib has no types for it.
interface RecognitionResult {
  readonly isFinal: boolean;
  readonly 0: { readonly transcript: string };
}
interface RecognitionEvent {
  readonly results: ArrayLike<RecognitionResult>;
}
interface Recognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: RecognitionEvent) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}
type RecognitionCtor = new () => Recognition;

const Ctor: RecognitionCtor | undefined =
  typeof window === 'undefined'
    ? undefined
    : ((window as unknown as { SpeechRecognition?: RecognitionCtor; webkitSpeechRecognition?: RecognitionCtor }).SpeechRecognition ??
      (window as unknown as { webkitSpeechRecognition?: RecognitionCtor }).webkitSpeechRecognition);

// Phones' continuous recognition repeats earlier words in later results; there each session is one utterance and the
// next one starts when it ends.
const PHONE =
  typeof navigator !== 'undefined' &&
  (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));

const ERRORS: Record<string, string> = {
  'not-allowed': t('The browser blocks the microphone. Allow it for this site in the address bar.'),
  'service-not-allowed': t('The browser blocks speech recognition for this site.'),
  'audio-capture': t('No microphone found.'),
  network: t('Speech recognition needs an internet connection.'),
};

/** Errors after which listening again doesn't help. */
const FATAL = new Set(['not-allowed', 'service-not-allowed', 'audio-capture', 'network', 'language-not-supported']);

/** Sessions in a row that end within a second without a word before dictation gives up. */
const MAX_EMPTY_SESSIONS = 3;

/** A session's words. A result that starts with the text so far replaces it (Android repeats earlier results). */
function transcript(results: ArrayLike<RecognitionResult>): string {
  let text = '';
  for (const res of Array.from(results)) {
    const part = res[0].transcript.trim();
    if (!part) continue;
    text = text && !part.toLowerCase().startsWith(text.toLowerCase()) ? `${text} ${part}` : part;
  }
  return text;
}

/**
 * Dictation into a text field (docs/features/conversations.md#dictation). With an OpenAI API runtime chosen for the
 * account (Settings → Account → Dictation) the person records and the recording comes back as text; otherwise the
 * browser recognises the speech. `supported` is false where neither works; hide the control then. `busy`: a
 * recording is being transcribed.
 */
export function useDictation(value: string, onChange: (text: string) => void, onError?: (message: string) => void) {
  const accountId = usePageAccountId();
  const settings = useQuery({
    queryKey: qk.dictation(accountId ?? ''),
    queryFn: () => api.dictation.settings(accountId!),
    enabled: !!accountId && CAN_RECORD,
    staleTime: 5 * 60_000,
  });
  const recorded = useRecordedDictation(accountId, value, onChange, onError);
  const browser = useBrowserDictation(value, onChange, onError);
  // A recording under way keeps its mode while the settings change.
  return (CAN_RECORD && settings.data?.runtime?.usable) || recorded.listening || recorded.busy ? recorded : browser;
}

/** The account of the page: from the workspace in the address, or the account's slug. */
function usePageAccountId(): string | undefined {
  const params = useParams({ strict: false }) as { account?: string; workspaceId?: string };
  const me = useMe();
  const workspace = useWorkspace(params.workspaceId ?? '');
  if (params.workspaceId) return workspace.data?.accountId;
  return params.account && me.data ? findAccount(me.data.accounts, params.account)?.id : undefined;
}

/**
 * The browser's speech recognition: speech is appended to `value` while the
 * person talks (interim words included) until `stop`/`toggle`. The browser ends
 * its recognition after a pause or a minute; dictation then listens again on its
 * own. Typing during dictation keeps what was typed, the next words go after
 * it. Language is the app's.
 */
function useBrowserDictation(value: string, onChange: (text: string) => void, onError?: (message: string) => void) {
  const [listening, setListening] = useState(false);
  const recognition = useRef<Recognition | null>(null);
  /** Ends the current session to listen again from what the person typed. */
  const retype = useRef<(() => void) | null>(null);
  const wanted = useRef(false);
  /** The text dictation last put in the field, to tell typing from it. */
  const written = useRef<string | null>(null);
  const latest = useRef({ value, onChange, onError });
  latest.current = { value, onChange, onError };

  const listen = useCallback((first: boolean, emptySessions = 0) => {
    if (!Ctor) return;
    const base = latest.current.value;
    const sep = base && !/\s$/.test(base) ? ' ' : '';
    const startedAt = Date.now();
    let heard = false;
    let fatal = false;
    let typedOver = false;
    const r = new Ctor();
    r.lang = intlLocale;
    r.continuous = !PHONE;
    r.interimResults = true;
    r.onresult = (e) => {
      const spoken = transcript(e.results);
      if (spoken) heard = true;
      const text = spoken ? base + sep + spoken : base;
      written.current = text;
      latest.current.onChange(text);
    };
    r.onerror = (e) => {
      if (!FATAL.has(e.error)) return;
      fatal = true;
      // A later session failing means the browser stopped it, not that the microphone is blocked.
      if (first) latest.current.onError?.(ERRORS[e.error] ?? t('Dictation stopped ({error}).', { error: e.error }));
    };
    r.onend = () => {
      if (recognition.current !== r) return;
      recognition.current = null;
      const empty = !heard && !typedOver && Date.now() - startedAt < 1000 ? emptySessions + 1 : 0;
      if (wanted.current && !fatal && empty < MAX_EMPTY_SESSIONS) {
        listen(false, empty);
      } else {
        wanted.current = false;
        written.current = null;
        setListening(false);
      }
    };
    recognition.current = r;
    retype.current = () => {
      if (typedOver) return;
      typedOver = true;
      r.onresult = null;
      r.abort();
    };
    try {
      r.start();
    } catch {
      recognition.current = null;
      wanted.current = false;
      setListening(false);
    }
  }, []);

  const start = useCallback(() => {
    if (!Ctor || wanted.current) return;
    wanted.current = true;
    written.current = latest.current.value;
    setListening(true);
    listen(true);
  }, [listen]);

  /** Ends dictation; `discard` drops words still being recognised (use before clearing the field). */
  const stop = useCallback((discard = false) => {
    if (!wanted.current) return;
    wanted.current = false;
    const r = recognition.current;
    if (!r) {
      written.current = null;
      setListening(false);
    } else if (discard) {
      r.onresult = null;
      r.abort();
    } else {
      r.stop();
    }
  }, []);

  // The person typed: the words being recognised would overwrite it, so listen again from the edited text.
  useEffect(() => {
    if (!wanted.current || !recognition.current || written.current === null || value === written.current) return;
    written.current = value;
    retype.current?.();
  }, [value]);

  useEffect(
    () => () => {
      wanted.current = false;
      recognition.current?.abort();
    },
    [],
  );

  const toggle = useCallback(() => (wanted.current ? stop() : start()), [start, stop]);
  return { supported: !!Ctor, listening, busy: false, start, stop, toggle };
}

const CAN_RECORD = typeof window !== 'undefined' && typeof MediaRecorder !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
/** Chrome and Firefox record webm, Safari mp4. */
const RECORDING_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
/** Longest recording; the Control Plane takes about 10 MB of audio. */
const MAX_RECORDING_MS = 10 * 60_000;
/** Shorter recordings are a slip of the finger. */
const MIN_RECORDING_MS = 500;

const MEDIA_ERRORS: Record<string, string> = { NotAllowedError: ERRORS['not-allowed'], SecurityError: ERRORS['not-allowed'], NotFoundError: ERRORS['audio-capture'] };

function base64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

/**
 * Recording, transcribed by the account's dictation runtime when the person stops: the text goes after what is in
 * the field then.
 */
function useRecordedDictation(accountId: string | undefined, value: string, onChange: (text: string) => void, onError?: (message: string) => void) {
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorder = useRef<MediaRecorder | null>(null);
  const starting = useRef(false);
  /** Counts discards: a recording or transcription from before one is dropped. */
  const generation = useRef(0);
  const latest = useRef({ value, onChange, onError });
  latest.current = { value, onChange, onError };

  const transcribe = useCallback(
    async (blob: Blob, gen: number) => {
      if (!accountId) return;
      setBusy(true);
      try {
        const { text } = await api.dictation.transcribe(accountId, { audioBase64: await base64(blob), mimeType: blob.type, language: locale });
        if (gen !== generation.current || !text) return;
        const base = latest.current.value;
        latest.current.onChange(base + (base && !/\s$/.test(base) ? ' ' : '') + text);
      } catch (err) {
        if (gen === generation.current) latest.current.onError?.(t('The recording could not be transcribed: {error}', { error: errorMessage(err) }));
      } finally {
        if (gen === generation.current) setBusy(false);
      }
    },
    [accountId],
  );

  const start = useCallback(async () => {
    if (!CAN_RECORD || !accountId || recorder.current || starting.current) return;
    starting.current = true;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      const name = (err as DOMException).name;
      latest.current.onError?.(MEDIA_ERRORS[name] ?? t('Dictation stopped ({error}).', { error: name }));
      return;
    } finally {
      starting.current = false;
    }
    const mimeType = RECORDING_TYPES.find((m) => MediaRecorder.isTypeSupported(m));
    const r = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    const chunks: Blob[] = [];
    const gen = generation.current;
    const startedAt = Date.now();
    const limit = setTimeout(() => r.state !== 'inactive' && r.stop(), MAX_RECORDING_MS);
    r.ondataavailable = (e) => {
      if (e.data.size) chunks.push(e.data);
    };
    r.onstop = () => {
      clearTimeout(limit);
      stream.getTracks().forEach((track) => track.stop());
      if (recorder.current === r) recorder.current = null;
      setListening(false);
      if (gen !== generation.current || Date.now() - startedAt < MIN_RECORDING_MS || !chunks.length) return;
      void transcribe(new Blob(chunks, { type: r.mimeType || mimeType || 'audio/webm' }), gen);
    };
    recorder.current = r;
    r.start();
    setListening(true);
  }, [accountId, transcribe]);

  /** Ends the recording and transcribes it; `discard` drops it and a transcription under way (use before clearing the field). */
  const stop = useCallback((discard = false) => {
    if (discard) {
      generation.current++;
      setBusy(false);
    }
    const r = recorder.current;
    if (r && r.state !== 'inactive') r.stop();
  }, []);

  useEffect(
    () => () => {
      generation.current++;
      const r = recorder.current;
      if (r && r.state !== 'inactive') r.stop();
    },
    [],
  );

  const toggle = useCallback(() => {
    if (recorder.current) stop();
    else if (!busy) void start();
  }, [busy, start, stop]);
  return { supported: CAN_RECORD && !!accountId, listening, busy, start: () => void start(), stop, toggle };
}
